import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { RouletteResult } from '@/components/roulette/RouletteResult'
import { RouletteWheel } from '@/components/roulette/RouletteWheel'
import { Button } from '@/components/ui'
import { useUpdateBook } from '@/hooks/books'
import { useRoulette, useSpinRoulette } from '@/hooks/roulette/useRoulettes'
import { captureError } from '@/lib/monitoring'
import { todayISO } from '@/lib/utils'
import type { Book } from '@/types/book'
import type { RouletteWithStats } from '@/types/roulette'

// Get available books (not yet spun)
function getAvailableBooks(rouletteDetails?: RouletteWithStats): Book[] {
  if (!rouletteDetails?.books) return []

  const spunBookIds = new Set(
    rouletteDetails.roulette_spins?.map((spin) => spin.selected_book_id) || []
  )
  return rouletteDetails.books.filter((book) => !spunBookIds.has(book.id))
}

export function RouletteSpinPage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [isSpinning, setIsSpinning] = useState(false)
  const [selectedBookId, setSelectedBookId] = useState<string | null>(null)
  const [spinDuration] = useState(4000)
  const [showResult, setShowResult] = useState(false)

  const { data: rouletteDetails } = useRoulette(id)
  const spinRouletteMutation = useSpinRoulette()
  const updateBookMutation = useUpdateBook()

  useEffect(() => {
    if (!id) {
      navigate('/dashboard/roulette')
    }
  }, [id, navigate])

  const handleSpin = () => {
    if (!rouletteDetails?.books || rouletteDetails.books.length < 1) {
      return
    }

    const availableBooks = getAvailableBooks(rouletteDetails)

    if (availableBooks.length === 0) {
      // All books have been spun, redirect to progress
      navigate(`/dashboard/roulette/${id}`)
      return
    }

    setIsSpinning(true)
    setShowResult(false)

    setTimeout(() => {
      const randomIndex = Math.floor(Math.random() * availableBooks.length)
      const selectedBook = availableBooks[randomIndex]
      setSelectedBookId(selectedBook.id)
      setIsSpinning(false)

      if (id) {
        spinRouletteMutation.mutate({
          rouletteId: id,
          selectedBookId: selectedBook.id,
          duration: spinDuration,
        })
      }

      setTimeout(() => {
        setShowResult(true)
      }, 1000)
    }, spinDuration)
  }

  const handleMarkAsNextRead = async () => {
    const selectedBook = getSelectedBook()
    if (!selectedBook || !id) return

    try {
      await updateBookMutation.mutateAsync({
        bookId: selectedBook.id,
        data: {
          status: 'reading',
          start_date: todayISO(),
        },
      })
      navigate(`/dashboard/roulette/${id}`)
    } catch (error) {
      captureError(error as Error, { context: 'Failed to update book status' })
    }
  }

  const handleViewProgress = () => {
    if (id) {
      navigate(`/dashboard/roulette/${id}`)
    }
  }

  const getSelectedBook = (): Book | null => {
    if (!selectedBookId || !rouletteDetails?.books) return null
    return rouletteDetails.books.find((book) => book.id === selectedBookId) || null
  }

  const getRouletteProgress = () => {
    if (!rouletteDetails) return { available: 0, total: 0, spun: 0, isCompleted: false }

    const spunBookIds = new Set(
      rouletteDetails.roulette_spins?.map((spin) => spin.selected_book_id) || []
    )
    const spunCount = spunBookIds.size
    const availableCount = rouletteDetails.book_count - spunCount
    const isCompleted = availableCount === 0 && rouletteDetails.book_count > 0

    return {
      available: availableCount,
      total: rouletteDetails.book_count,
      spun: spunCount,
      isCompleted,
    }
  }

  const selectedBook = getSelectedBook()
  const progress = getRouletteProgress()
  const availableBooks = getAvailableBooks(rouletteDetails)

  if (!rouletteDetails) {
    return (
      <div className="h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-primary-dark">
          <div className="absolute inset-0 bg-popover/20"></div>
        </div>
        <div className="relative z-10 text-center">
          <p className="text-primary-foreground">{t('common.loading')}</p>
        </div>
      </div>
    )
  }

  if (showResult && selectedBook) {
    return (
      <RouletteResult
        selectedBook={selectedBook}
        onMarkAsNextRead={handleMarkAsNextRead}
        onViewHistory={handleViewProgress}
      />
    )
  }

  return (
    <div className="fixed inset-0 lg:left-64 bg-gradient-to-br from-primary via-primary to-primary-dark overflow-hidden">
      <div className="absolute inset-0 bg-popover/20"></div>

      <div className="flex items-center justify-center h-full">
        <div className="relative z-10 text-center">
          <div className="mb-8 text-primary-foreground">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center text-xl"
                style={{ backgroundColor: `${rouletteDetails.color}40` }}
              >
                {rouletteDetails.icon}
              </div>
              <h1 className="text-3xl font-bold text-primary-foreground">{rouletteDetails.name}</h1>
            </div>
          </div>

          <RouletteWheel
            books={availableBooks}
            isSpinning={isSpinning}
            selectedBookId={selectedBookId || undefined}
            duration={spinDuration}
            onSpinComplete={() => {
              setIsSpinning(false)
            }}
          />

          <div className="mt-8 text-primary-foreground">
            {!isSpinning && !progress.isCompleted && (
              <Button
                onClick={handleSpin}
                disabled={isSpinning || progress.isCompleted}
                className="bg-card text-foreground hover:bg-muted px-8 py-4 rounded-full font-bold text-xl shadow-lg transform transition hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                🎰 {t('roulette.spinWheel')}
              </Button>
            )}

            {isSpinning && (
              <div className="space-y-4">
                <p className="text-primary-foreground/80">{t('roulette.spinningDescription')}</p>
                <div className="flex justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-foreground"></div>
                </div>
              </div>
            )}

            {progress.isCompleted && (
              <div className="bg-success/20 border border-success rounded-lg p-4">
                <p className="text-success font-semibold">✅ {t('roulette.completed')}</p>
                <p className="text-success text-sm mt-1">{t('roulette.allBooksSpun')}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 right-6 flex gap-3">
        <Button
          onClick={handleViewProgress}
          className="w-14 h-14 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg"
          title={t('roulette.viewProgress')}
        >
          📊
        </Button>
      </div>
    </div>
  )
}
